import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import BookingApi from "../../API/Apis/BookingApi";
import BookingGetters from "../../store/onlineBooking/getters";
import { ShareboxPickupResponse } from "../../@types/DTO/Sharebox";
import ShareboxHeader from "./components/ShareboxHeader";
import ShareboxFooter from "./components/ShareboxFooter";

type Props = {
  token: string;
  urlToken?: string;
  pickup: ShareboxPickupResponse;
  onOpened: () => void;
  onBack?: () => void;
};

const FALLBACK_QR_VALUE = "SHAREBOX-QR-FALLBACK";
const OPEN_STATUS_POLL_INTERVAL_MS = 3000;

export default function D8OpenBox({
  token,
  urlToken,
  pickup,
  onOpened,
  onBack,
}: Props) {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;
  const [showTroubleHelp, setShowTroubleHelp] = useState(false);
  const { cabinet, boxNumber, locationName } = pickup.booking;

  const [qrBytes, setQrBytes] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(true);
  const qrFetchedRef = useRef(false);
  useEffect(() => {
    if (qrFetchedRef.current || !urlToken) {
      setQrLoading(false);
      return;
    }
    qrFetchedRef.current = true;
    BookingApi.getShareboxDriverQr(urlToken)
      .then((response) => setQrBytes(response.data?.qrBytes || null))
      .catch((error) => {
        console.error("Failed to fetch the driver QR:", error);
      })
      .finally(() => setQrLoading(false));
  }, [urlToken]);

  const onOpenedRef = useRef(onOpened);
  useEffect(() => {
    onOpenedRef.current = onOpened;
  }, [onOpened]);

  // A recorded open means the QR was scanned and the box opened: move on,
  // exactly as the former "Open box" button did.
  useEffect(() => {
    if (!token) return;
    let stopped = false;
    let inFlight = false;

    const poll = async () => {
      if (stopped || inFlight || document.hidden) return;
      inFlight = true;
      try {
        const response = await BookingApi.getShareboxOpenStatus(token);
        if (stopped) return;
        if (response.data?.openedAt) {
          stopped = true;
          clearInterval(intervalId);
          onOpenedRef.current();
        }
      } catch (error) {
        console.error("Failed to fetch the box open status:", error);
      } finally {
        inFlight = false;
      }
    };

    const intervalId = setInterval(poll, OPEN_STATUS_POLL_INTERVAL_MS);
    poll();
    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [token]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <ShareboxHeader
        logoUrl={appSettings?.config?.logoUrl}
        rightSlot={`${translate("Step")} 3 ${translate("of")} 3`}
        onBack={onBack}
      />

      <Box
        sx={{ maxWidth: 480, mx: "auto", px: 3, py: 4, textAlign: "center" }}
      >
        <CheckCircleIcon
          sx={{ fontSize: 56, color: "success.main", mb: 1.5 }}
        />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {translate("Contract signed")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate("You're ready for pickup.")}
        </Typography>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            p: 3,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: webCheckinSecondaryColor || "#E0007A",
              mb: 0.5,
            }}
          >
            {translate("Open the box")} {boxNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate(
              "Show this QR code to the Sharebox reader: the box will open automatically."
            )}
          </Typography>
          <Box
            data-testid="driver-qr-display"
            sx={{
              width: 160,
              height: 160,
              mx: "auto",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {/* {qrLoading ? (
              <CircularProgress size={32} />
            ) : (
              <QRCodeSVG
                value={qrBytes || FALLBACK_QR_VALUE}
                size={140}
                level="M"
              />
            )} */}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5 }}
          >
            {cabinet} · {locationName}
          </Typography>
        </Box>

        <Box>
          <Box
            component="button"
            type="button"
            onClick={() => setShowTroubleHelp((prev) => !prev)}
            sx={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontWeight: 700,
              textDecoration: "underline",
              color: webCheckinSecondaryColor || "#E0007A",
              fontSize: "0.95rem",
              fontFamily: "inherit",
            }}
          >
            {translate("Having trouble opening the box?")}
          </Box>
          {showTroubleHelp && (
            <Alert severity="info" sx={{ mt: 1.5, textAlign: "left" }}>
              {translate(
                "If the box doesn't open, please go to our {{location}} office for pickup assistance.",
                { location: locationName }
              )}
            </Alert>
          )}
        </Box>
      </Box>
      <ShareboxFooter />
    </Box>
  );
}
